import { useState, useEffect } from 'react';

/**
 * Minimal contact shape for CommandBar search (privacy-conscious)
 */
export interface CommandBarContact {
  id: number;
  clientId?: number | null;
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  email: string | null;
}

/**
 * Minimal trip shape for CommandBar search
 */
export interface CommandBarTrip {
  id: number;
  linkId?: number | null;
  name: string | null;
  clientName: string | null;
  countries: string[] | null;
  startDate: string | null;
  endDate: string | null;
}

export interface CommandBarSearchData {
  contactsData: CommandBarContact[];
  tripsData: CommandBarTrip[];
  searchDataLoaded: boolean;
}

// Module-level cache to prevent duplicate fetches across component mounts
let cachedContactsData: CommandBarContact[] | null = null;
let cachedTripsData: CommandBarTrip[] | null = null;
let fetchPromise: Promise<void> | null = null;

/**
 * Shared hook to load search data for CommandBar context cards.
 * - Fetches contacts and trips once per session (cached at module level).
 * - Multiple components calling this hook share the same cached data.
 * - Privacy-conscious: only exposes minimal fields needed for search.
 */
export function useCommandBarSearchData(): CommandBarSearchData {
  const [contactsData, setContactsData] = useState<CommandBarContact[]>(cachedContactsData ?? []);
  const [tripsData, setTripsData] = useState<CommandBarTrip[]>(cachedTripsData ?? []);
  const [searchDataLoaded, setSearchDataLoaded] = useState(cachedContactsData !== null);

  useEffect(() => {
    // If already cached, just use it
    if (cachedContactsData !== null && cachedTripsData !== null) {
      setContactsData(cachedContactsData);
      setTripsData(cachedTripsData);
      setSearchDataLoaded(true);
      return;
    }

    // If a fetch is already in progress, wait for it
    if (fetchPromise) {
      fetchPromise.then(() => {
        setContactsData(cachedContactsData ?? []);
        setTripsData(cachedTripsData ?? []);
        setSearchDataLoaded(true);
      });
      return;
    }

    // Start the fetch
    const loadSearchData = async () => {
      try {
        const [contactsRes, tripsRes] = await Promise.all([
          fetch('/api/clients?limit=500', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch('/api/trips?limit=200', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);

        if (contactsRes.ok && tripsRes.ok) {
          const contactsResponse = await contactsRes.json();
          const tripsResponse = await tripsRes.json();
          
          // Handle both response formats (array or object with contacts property)
          const rawContactsData = Array.isArray(contactsResponse) 
            ? contactsResponse 
            : contactsResponse?.contacts || [];
          
          // Handle both response formats for trips (array or object with trips property)
          const rawTripsData = Array.isArray(tripsResponse) 
            ? tripsResponse 
            : tripsResponse?.trips || tripsResponse || [];
          
          // Strip sensitive fields client-side (keep only search-needed fields)
          cachedContactsData = rawContactsData.map((contact: any) => ({
            id: contact.id,
            clientId: contact.clientId ?? null,
            firstName: contact.firstName,
            lastName: contact.lastName,
            preferredName: contact.preferredName,
            email: contact.email
          }));
          
          // Strip sensitive fields from trips (keep only search-needed fields)
          cachedTripsData = rawTripsData.map((trip: any) => ({
            id: trip.id,
            linkId: trip.linkId ?? null,
            name: trip.name,
            clientName: trip.clientName,
            countries: trip.countries,
            startDate: trip.startDate,
            endDate: trip.endDate
          }));
        } else {
          // On failure, cache empty arrays to prevent retry loops
          cachedContactsData = [];
          cachedTripsData = [];
        }
      } catch (error) {
        console.error('Failed to load CommandBar search data:', error);
        // On error, cache empty arrays
        cachedContactsData = [];
        cachedTripsData = [];
      }
    };

    fetchPromise = loadSearchData().finally(() => {
      setContactsData(cachedContactsData ?? []);
      setTripsData(cachedTripsData ?? []);
      setSearchDataLoaded(true);
      fetchPromise = null;
    });
  }, []);

  return { contactsData, tripsData, searchDataLoaded };
}

